import * as React from "react";
import { cn } from "@/lib/utils";

// `ComponentPropsWithRef` rather than `InputHTMLAttributes` so a caller can hold the element:
// React 19 passes `ref` through as an ordinary prop, and the spread below already carries it.
export interface InputProps extends React.ComponentPropsWithRef<"input"> {
  /** Renders the error state; the message itself belongs on the surrounding <Field>. */
  invalid?: boolean;
  /** Icon or button rendered inside the field, right-aligned. */
  trailing?: React.ReactNode;
  leading?: React.ReactNode;
}

export function Input({ className, invalid, trailing, leading, ...props }: InputProps) {
  const input = (
    <input
      className={cn(
        "h-8 w-full rounded-md border bg-surface px-2.5 text-base text-text shadow-xs",
        "transition-[border-color,box-shadow] duration-100",
        "placeholder:text-text-subtle",
        "focus-visible:border-accent-8 focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-accent-8/25",
        "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-muted",
        // Chrome's autofill styling ignores background-color; the inset shadow trick is the
        // only reliable way to keep an autofilled field on-theme.
        "[&:-webkit-autofill]:[-webkit-text-fill-color:var(--color-text)]",
        "[&:-webkit-autofill]:[box-shadow:0_0_0_1000px_var(--color-surface)_inset]",
        invalid
          ? "border-danger-9 focus-visible:border-danger-9 focus-visible:ring-danger-9/25"
          : "border-border",
        leading && "pl-8",
        trailing && "pr-8",
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );

  if (!leading && !trailing) return input;
  return (
    <div className="relative w-full">
      {leading && (
        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-subtle [&_svg]:size-4">
          {leading}
        </span>
      )}
      {input}
      {trailing && (
        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-text-subtle [&_svg]:size-4">
          {trailing}
        </span>
      )}
    </div>
  );
}

// `ComponentPropsWithRef`, as `Input` is and for the same reason: a caller that writes into the
// text where the caret is has to hold the element.
export function Textarea({
  className, invalid, ...props
}: React.ComponentPropsWithRef<"textarea"> & { invalid?: boolean }) {
  return (
    <textarea
      className={cn(
        "min-h-20 w-full rounded-md border bg-surface px-2.5 py-2 font-mono text-xs text-text",
        "shadow-xs transition-[border-color,box-shadow] duration-100",
        "placeholder:text-text-subtle focus-visible:border-accent-8 focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-accent-8/25",
        "disabled:cursor-not-allowed disabled:bg-surface-sunken",
        invalid ? "border-danger-9" : "border-border",
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}
